import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import paymentTermService, {
  CreatePaymentTermPayload,
  PaymentTermFilters,
} from '@/services/paymentTerm.service';

export const PAYMENT_TERMS_QUERY_KEY = 'payment-terms';
export const PAYMENT_TERMS_SELECTION_QUERY_KEY = 'payment-terms-selection';

export function usePaymentTerms(filters?: PaymentTermFilters) {
  return useQuery({
    queryKey: [PAYMENT_TERMS_QUERY_KEY, filters],
    queryFn: () => paymentTermService.list(filters),
    staleTime: 30000,
  });
}

export function usePaymentTermsForSelection() {
  return useQuery({
    queryKey: [PAYMENT_TERMS_SELECTION_QUERY_KEY],
    queryFn: () => paymentTermService.getForSelection(),
    staleTime: 60000,
  });
}

export function useCreatePaymentTerm() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreatePaymentTermPayload) => paymentTermService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_SELECTION_QUERY_KEY] });
      message.success('Payment term created');
    },
    onError: (error: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create payment term');
    },
  });
}

export function useUpdatePaymentTerm() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreatePaymentTermPayload> }) =>
      paymentTermService.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_SELECTION_QUERY_KEY] });
      message.success('Payment term updated');
    },
    onError: (error: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update payment term');
    },
  });
}

export function useDeletePaymentTerm() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => paymentTermService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENT_TERMS_SELECTION_QUERY_KEY] });
      message.success('Payment term deleted');
    },
    onError: (error: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to delete payment term');
    },
  });
}
